import i18n, { createInstance, type i18n as I18nInstance, type InitOptions } from 'i18next';
import HttpApi, { type HttpBackendOptions } from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

import { Logger, LogLevel } from '@repo/utils/common';

import { appEnv, type AppLocale } from '@/shared/configs/app.config';
import { getLocaleFromRequest } from '@/shared/utils/i18n.utils';

const logger = new Logger(LogLevel.DEBUG);

const i18nOptions: InitOptions<HttpBackendOptions> = {
  fallbackLng: false,
  interpolation: {
    escapeValue: false,
  },
  backend: {
    loadPath: `${appEnv.VITE_API_URL}/config/{{lng}}/translations`,
    withCredentials: true,
  },
};

/**
 * Languages supported by the portal.
 * Used for redirecting to the correct language in the portal.
 */
export const portalLanguages = {
  en: 'en',
  sl: 'sl_SI',
  de: 'de_DE',
  fr: 'fr_FR',
  pl: 'pl_PL',
} as const;

/**
 * Creates a new i18n instance for server-side rendering.
 * Each request should get its own instance to avoid shared state.
 */
export const createI18nServerInstance = async (request: Request): Promise<I18nInstance> => {
  const lng = getLocaleFromRequest(request);
  const i18n = createInstance();

  await i18n
    .use(initReactI18next)
    .init({ ...i18nOptions, lng })
    .catch((error) => {
      logger.error('Failed to initialize i18n instance:', error);
    });

  return i18n;
};

/**
 * Creates a new i18n instance for client-side rendering.
 * This function is called on the client side, before the app is hydrated.
 */
export const createI18nClientInstance = async (lng: AppLocale): Promise<I18nInstance> => {
  await i18n
    .use(HttpApi)
    .use(initReactI18next)
    .init({ ...i18nOptions, lng })
    .catch((error) => {
      logger.error('Failed to initialize i18n instance:', error);
    });

  return i18n;
};

export { i18n };
